import React, { useRef, useState, useEffect, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

/**
 * HorizontalScroll
 * ------------------------------------------------------------------
 * Carrusel horizontal premium (estilo Netflix / Airbnb / QuintoAndar)
 * para listados de inmuebles u otro contenido en tarjetas.
 *
 * - Auto-scroll con pausa en hover, scroll-snap nativo, flechas en
 *   desktop, swipe nativo en mobile, gradientes de fade en los
 *   bordes y animaciones de entrada con stagger via framer-motion.
 *
 * Uso:
 * <HorizontalScroll title="Imóveis em Destaque" subtitle="Selecionados para você" onViewAll={() => {}}>
 *   {imoveis.map((imovel) => <PropertyCard key={imovel.id} {...imovel} />)}
 * </HorizontalScroll>
 */

const AUTO_SCROLL_INTERVAL = 4000; // ms
const AUTO_SCROLL_STEP = 300; // px por "tick" de auto-scroll
const EDGE_THRESHOLD = 10; // px de tolerancia para detectar inicio/fin

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const trackVariants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.08 },
  },
};

const HorizontalScroll = memo(function HorizontalScroll({
  title,
  subtitle,
  children,
  onViewAll,
  autoScroll = true,
  showArrows = true,
  showDots = false,
  showGradient = true,
  primaryColor = '#0EA5E9',
  className = '',
}) {
  const scrollRef = useRef(null);
  const autoScrollRef = useRef(null);
  const isHoveringRef = useRef(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeDot, setActiveDot] = useState(0);

  const childrenArray = React.Children.toArray(children);
  const dotCount = childrenArray.length;

  // ---------------------------------------------------------------
  // Detección de posición de scroll (flechas / gradientes / dots)
  // ---------------------------------------------------------------
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > EDGE_THRESHOLD);
    setCanScrollRight(container.scrollLeft < maxScroll - EDGE_THRESHOLD);

    if (showDots && dotCount > 0) {
      const cardWidth = container.children[0]?.offsetWidth || 1;
      const idx = Math.round(container.scrollLeft / cardWidth);
      setActiveDot(Math.min(idx, dotCount - 1));
    }
  }, [showDots, dotCount]);

  // ---------------------------------------------------------------
  // Scroll manual (flechas)
  // ---------------------------------------------------------------
  const scroll = useCallback((direction) => {
    const container = scrollRef.current;
    if (!container) return;

    const cardWidth = container.children[0]?.offsetWidth || 300;
    const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  const scrollLeft = useCallback(() => scroll('left'), [scroll]);
  const scrollRight = useCallback(() => scroll('right'), [scroll]);

  // ---------------------------------------------------------------
  // Auto-scroll con pausa en hover y loop infinito
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!autoScroll) return undefined;

    const container = scrollRef.current;
    if (!container) return undefined;

    const onEnter = () => {
      isHoveringRef.current = true;
    };
    const onLeave = () => {
      isHoveringRef.current = false;
    };

    container.addEventListener('mouseenter', onEnter);
    container.addEventListener('mouseleave', onLeave);

    autoScrollRef.current = setInterval(() => {
      if (isHoveringRef.current) return;
      const current = scrollRef.current;
      if (!current) return;

      const maxScroll = current.scrollWidth - current.clientWidth;
      if (current.scrollLeft >= maxScroll - EDGE_THRESHOLD) {
        current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: AUTO_SCROLL_STEP, behavior: 'smooth' });
      }
    }, AUTO_SCROLL_INTERVAL);

    return () => {
      clearInterval(autoScrollRef.current);
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
    };
  }, [autoScroll]);

  // Inicializa el estado de flechas/gradientes al montar y cuando cambian los hijos
  useEffect(() => {
    handleScroll();
  }, [handleScroll, dotCount]);

  const hasContent = dotCount > 0;

  return (
    <section className={`relative w-full ${className}`}>
      {/* Header */}
      <motion.div
        className="flex items-end justify-between gap-4 px-4 md:px-6 mb-4 md:mb-5"
        variants={headerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
      >
        <div className="min-w-0">
          {title && (
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight truncate">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {showArrows && hasContent && (
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                aria-label="Rolar para a esquerda"
                className={`flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700
                  bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200
                  transition-all duration-200 ease-out
                  hover:scale-110 active:scale-95
                  disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100
                  shadow-sm hover:shadow-md`}
              >
                <ChevronLeft className="w-4.5 h-4.5" size={18} />
              </button>
              <button
                type="button"
                onClick={scrollRight}
                disabled={!canScrollRight}
                aria-label="Rolar para a direita"
                className={`flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700
                  bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200
                  transition-all duration-200 ease-out
                  hover:scale-110 active:scale-95
                  disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100
                  shadow-sm hover:shadow-md`}
              >
                <ChevronRight className="w-4.5 h-4.5" size={18} />
              </button>
            </div>
          )}

          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="group flex items-center gap-1 text-sm font-semibold whitespace-nowrap"
              style={{ color: primaryColor }}
            >
              Ver todos
              <ArrowRight
                className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1"
              />
            </button>
          )}
        </div>
      </motion.div>

      {/* Scroll track + gradientes */}
      <motion.div
        className="relative"
        variants={trackVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
      >
        {/* Gradiente izquierdo */}
        {showGradient && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute left-0 top-0 bottom-0 z-10 hidden md:block w-16 md:w-24
              bg-gradient-to-r from-white dark:from-slate-950 to-white dark:to-slate-950
              transition-opacity duration-300
              ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`}
          />
        )}

        {/* Gradiente derecho */}
        {showGradient && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute right-0 top-0 bottom-0 z-10 hidden md:block w-16 md:w-24
              bg-gradient-to-l from-white dark:from-slate-950 to-white dark:to-slate-950
              transition-opacity duration-300
              ${canScrollRight ? 'opacity-100' : 'opacity-0'}`}
          />
        )}

        <motion.div
          ref={scrollRef}
          onScroll={handleScroll}
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="flex gap-4 md:gap-5 overflow-x-auto scrollbar-hide px-4 md:px-6 pb-2
            touch-pan-x will-change-transform"
          style={{
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {childrenArray.map((child, index) => (
            <motion.div
              key={child.key ?? index}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                },
              }}
              className="shrink-0 w-[85vw] xs:w-[70vw] sm:w-[340px] md:w-[300px] lg:w-[280px]"
              style={{
                scrollSnapAlign: 'start',
                transform: 'translateZ(0)',
              }}
            >
              {child}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Dots */}
      {showDots && hasContent && (
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {childrenArray.map((_, index) => (
            <span
              key={index}
              aria-hidden="true"
              className={`h-1.5 rounded-full transition-all duration-300 ease-out ${
                activeDot === index ? '' : 'bg-slate-300 dark:bg-slate-700'
              }`}
              style={{
                width: activeDot === index ? '20px' : '6px',
                backgroundColor: activeDot === index ? primaryColor : undefined,
              }}
            />
          ))}
        </div>
      )}

      {/* Utilidad local: oculta la scrollbar nativa sin dependencias extra */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
});

HorizontalScroll.displayName = 'HorizontalScroll';

export { HorizontalScroll };
export default HorizontalScroll;